import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api, setUnauthorizedHandler } from "../api/client";
import { identifyUser, resetAnalytics } from "./analytics";
import { forgetInvite } from "./invite";
import { ownEndpoint, pushBlock, unsubscribe as unsubscribePush } from "../pwa/push";
import { applyDelta } from "../shared/score";
import type { DayRecord, HouseholdSnapshot, Kid, KidSummary, PointEvent, SessionInfo } from "../shared/types";
import type { History } from "../shared/history";

/** How often an open, visible app re-reads the household. */
const LIVE_REFRESH_MS = 30_000;

/** "offline": the session could not be checked (no network, server down), so it is kept. */
type AuthState = "loading" | "signed_out" | "offline" | "ready";

interface Store {
  auth: AuthState;
  session: SessionInfo | null;
  snapshot: HouseholdSnapshot | null;
  refresh: () => Promise<void>;
  /** Tries the session check again after "offline". */
  retryAuth: () => void;
  requestCode: (email: string) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<void>;
  kidSignIn: (code: string) => Promise<void>;
  /** Preview-only one-tap sign-in (the server route 404s in production). */
  previewSignIn: () => Promise<void>;
  logout: () => Promise<void>;
  // Adult-only mutations. The Worker refuses them for a child session; the UI hides them.
  createKid: (name: string, colour: string) => Promise<Kid>;
  editKid: (kidId: string, name: string, colour: string) => Promise<void>;
  removeKid: (kidId: string, name: string) => Promise<void>;
  mintKidCode: (kidId: string) => Promise<string>;
  /** Stores a kid's photo (an image the phone has already cropped and shrunk), then re-reads. */
  setKidPhoto: (kidId: string, photo: Blob) => Promise<void>;
  removeKidPhoto: (kidId: string) => Promise<void>;
  inviteParent: (email: string) => Promise<void>;
  removeParent: (email: string) => Promise<void>;
  /** Moves the score on screen straight away, then settles on what the server stored. */
  addEvent: (kidId: string, delta: number, reason?: string | null) => Promise<PointEvent>;
  deleteEvent: (eventId: string) => Promise<void>;
  /** One kid's taps on a household day (YYYY-MM-DD), oldest first, and the score it opened on. */
  loadDayEvents: (kidId: string, day: string) => Promise<{ events: PointEvent[]; opening: number }>;
  setGoal: (kidId: string, goal: number) => Promise<void>;
  /**
   * Closes the day (today again if already closed; a closed past day comes back as it is) and
   * re-reads the stars. `fresh` is true only when this close won the star.
   */
  closeDay: (kidId: string, day: string) => Promise<DayRecord & { fresh: boolean }>;
  /** One kid's closed days from `from` to `to` inclusive, oldest first. Any session may read. */
  loadDays: (kidId: string, from: string, to: string) => Promise<DayRecord[]>;
  /** One kid's score day by day from `from` to `to`, for the Diary tab. Any session may read. */
  loadHistory: (kidId: string, from: string, to: string, signal?: AbortSignal) => Promise<History>;
}

/** The snapshot with one kid's summary changed. */
function patchKid(
  snap: HouseholdSnapshot | null,
  kidId: string,
  patch: (kid: KidSummary) => Partial<KidSummary>,
): HouseholdSnapshot | null {
  if (!snap) return snap;
  return { ...snap, kids: snap.kids.map((k) => (k.id === kidId ? { ...k, ...patch(k) } : k)) };
}

const Ctx = createContext<Store | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>("loading");
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [snapshot, setSnapshot] = useState<HouseholdSnapshot | null>(null);

  // Taps still waiting on the server. A snapshot is only applied once none are, so a reply to an
  // early read cannot drag the number back past a later tap that is still in flight.
  const pending = useRef(0);
  // Each refresh takes the next number and only the newest one may write, so a slow early
  // reply landing after a later one cannot put older stars or scores back on screen.
  const latestRefresh = useRef(0);

  const refresh = useCallback(async () => {
    const mine = ++latestRefresh.current;
    const snap = await api.snapshot();
    if (mine === latestRefresh.current && pending.current === 0) setSnapshot(snap);
  }, []);

  // The one way out: sign-out, an expired or revoked session, any 401 from any call. Resetting
  // analytics here keeps the next person on a shared phone from being merged into this one.
  const signedOut = useCallback(() => {
    resetAnalytics();
    setAuth("signed_out");
    setSession(null);
    setSnapshot(null);
  }, []);

  useEffect(() => setUnauthorizedHandler(signedOut), [signedOut]);

  // Re-read when the app comes back to the front, and every 30 seconds while it is on screen, so
  // a phone left open on the table catches up with the other grown-up's taps and closes, and
  // "today" rolls over at midnight (the server works out the day on every read). Nothing runs
  // while the app is hidden.
  useEffect(() => {
    if (auth !== "ready") return;
    let last = Date.now();
    let timer: number | undefined;
    const read = () => {
      // Coming back usually fires both focus and visibilitychange; one read is enough.
      const now = Date.now();
      if (now - last < 2000) return;
      last = now;
      refresh().catch(() => undefined);
    };
    const onVisibility = () => {
      window.clearInterval(timer);
      timer = undefined;
      if (document.visibilityState !== "visible") return;
      read();
      timer = window.setInterval(read, LIVE_REFRESH_MS);
    };
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", read);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", read);
    };
  }, [auth, refresh]);

  /**
   * Re-reads who is signed in. Only a 401 signs out (and rejects, after showing Login, so a
   * sign-in form can say why). Anything else (offline, the one-off 500 while a deploy restarts
   * the Worker) is retried briefly, then shown as "Can't reach Pointz" with the session kept,
   * so opening the app with no signal never asks anyone to sign in again.
   */
  const refreshAuth = useCallback(async () => {
    for (let attempt = 0; ; attempt++) {
      try {
        const me = await api.me();
        setSession(me);
        // Role only as a property: the distinct id already names a grown-up, and nothing about
        // a child goes beyond the kid:<id> the Worker uses too.
        identifyUser(me.role === "adult" ? me.email : `kid:${me.kidId}`, { role: me.role });
        await refresh();
        setAuth("ready");
        return;
      } catch (e) {
        const status = (e as { status?: number }).status;
        if (status === 401) {
          signedOut();
          throw e;
        }
        if (attempt < 3) {
          await new Promise((r) => setTimeout(r, 750 * 2 ** attempt));
          continue;
        }
        setAuth("offline");
        return;
      }
    }
  }, [refresh, signedOut]);

  useEffect(() => {
    // A failure here already shows Login; there is no caller to tell.
    refreshAuth().catch(() => undefined);
  }, [refreshAuth]);

  const retryAuth = useCallback(() => {
    setAuth("loading");
    refreshAuth().catch(() => undefined);
  }, [refreshAuth]);

  const requestCode = useCallback(async (email: string) => {
    await api.requestCode(email);
  }, []);

  const verifyCode = useCallback(
    async (email: string, code: string) => {
      await api.verifyCode(email, code);
      await refreshAuth();
    },
    [refreshAuth],
  );

  const kidSignIn = useCallback(
    async (code: string) => {
      await api.kidSignIn(code);
      await refreshAuth();
    },
    [refreshAuth],
  );

  const previewSignIn = useCallback(async () => {
    await api.previewLogin();
    await refreshAuth();
  }, [refreshAuth]);

  const logout = useCallback(async () => {
    // Here, not in signedOut: that also runs for the signed-out check on launch, which would
    // forget the invite before Login could prefill it.
    forgetInvite();
    try {
      // A signed-out phone should stop getting the household's pushes. Best effort: it needs
      // the session, so it goes first, and a failure must not keep anyone signed in.
      if (session?.role === "adult" && pushBlock() === null) await unsubscribePush().catch(() => undefined);
      await api.logout();
    } finally {
      signedOut();
    }
  }, [session, signedOut]);

  const createKid = useCallback(
    async (name: string, colour: string) => {
      const kid = await api.createKid(name, colour);
      await refresh();
      return kid;
    },
    [refresh],
  );

  const editKid = useCallback(
    async (kidId: string, name: string, colour: string) => {
      await api.editKid(kidId, name, colour);
      await refresh();
    },
    [refresh],
  );

  const removeKid = useCallback(
    async (kidId: string, name: string) => {
      await api.removeKid(kidId, name);
      await refresh();
    },
    [refresh],
  );

  const mintKidCode = useCallback(async (kidId: string) => (await api.mintKidCode(kidId)).code, []);

  const setKidPhoto = useCallback(
    async (kidId: string, photo: Blob) => {
      await api.setKidPhoto(kidId, photo);
      await refresh();
    },
    [refresh],
  );

  const removeKidPhoto = useCallback(
    async (kidId: string) => {
      await api.removeKidPhoto(kidId);
      await refresh();
    },
    [refresh],
  );

  const inviteParent = useCallback(
    async (email: string) => {
      await api.inviteParent(email);
      await refresh();
    },
    [refresh],
  );

  const removeParent = useCallback(
    async (email: string) => {
      await api.removeParent(email);
      await refresh();
    },
    [refresh],
  );

  const settle = useCallback(async () => {
    pending.current--;
    // A failed re-read must not hide the tap's own error; the next mutation re-reads anyway.
    if (pending.current === 0) await refresh().catch(() => undefined);
  }, [refresh]);

  const addEvent = useCallback(
    async (kidId: string, delta: number, reason: string | null = null) => {
      pending.current++;
      let moved = 0;
      const shift = (k: KidSummary, by: number) => ({
        score: k.score + by,
        todayNet: k.todayNet + by,
        week: { ...k.week, net: k.week.net + by },
      });
      setSnapshot((s) =>
        patchKid(s, kidId, (k) => {
          moved = applyDelta(k.score, delta) - k.score;
          return shift(k, moved);
        }),
      );
      try {
        const { event } = await api.addEvent(kidId, delta, reason, await ownEndpoint());
        return event;
      } catch (e) {
        // Take the tap back off the screen here: offline, the refresh below fails too and would
        // leave points showing that were never saved.
        setSnapshot((s) => patchKid(s, kidId, (k) => shift(k, -moved)));
        throw e;
      } finally {
        // On failure the refresh puts the number back to what the server holds.
        await settle();
      }
    },
    [settle],
  );

  const deleteEvent = useCallback(
    async (eventId: string) => {
      pending.current++;
      try {
        await api.deleteEvent(eventId);
      } finally {
        await settle();
      }
    },
    [settle],
  );

  const loadDayEvents = useCallback((kidId: string, day: string) => api.dayEvents(kidId, day), []);

  const setGoal = useCallback(
    async (kidId: string, goal: number) => {
      await api.setGoal(kidId, goal);
      await refresh();
    },
    [refresh],
  );

  const closeDay = useCallback(
    async (kidId: string, day: string) => {
      const record = await api.closeDay(kidId, day);
      // The day is closed whatever this re-read does; a failed one only delays the new stars.
      await refresh().catch(() => undefined);
      return record;
    },
    [refresh],
  );

  const loadDays = useCallback(
    async (kidId: string, from: string, to: string) => (await api.days(kidId, from, to)).days,
    [],
  );

  const loadHistory = useCallback(
    (kidId: string, from: string, to: string, signal?: AbortSignal) => api.history(kidId, from, to, signal),
    [],
  );

  const value = useMemo<Store>(
    () => ({
      auth,
      session,
      snapshot,
      refresh,
      retryAuth,
      requestCode,
      verifyCode,
      kidSignIn,
      previewSignIn,
      logout,
      createKid,
      editKid,
      removeKid,
      mintKidCode,
      setKidPhoto,
      removeKidPhoto,
      inviteParent,
      removeParent,
      addEvent,
      deleteEvent,
      loadDayEvents,
      setGoal,
      closeDay,
      loadDays,
      loadHistory,
    }),
    [
      auth,
      session,
      snapshot,
      refresh,
      retryAuth,
      requestCode,
      verifyCode,
      kidSignIn,
      previewSignIn,
      logout,
      createKid,
      editKid,
      removeKid,
      mintKidCode,
      setKidPhoto,
      removeKidPhoto,
      inviteParent,
      removeParent,
      addEvent,
      deleteEvent,
      loadDayEvents,
      setGoal,
      closeDay,
      loadDays,
      loadHistory,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStore(): Store {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
}
